import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import ContactForm from '../components/ContactForm';
import ResponsiveCarousel from '../components/ResponsiveCarousel';
import { services, pricingPackages, testimonials, faqs } from '../data/mock';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

// Testimonials Carousel Component
const TestimonialsCarousel = ({ testimonials }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true,
      align: 'start',
      slidesToScroll: 1
    },
    [Autoplay({ delay: 4000, stopOnInteraction: false })]
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  return (
    <div className="relative">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-6">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.333%] min-w-0">
              <Card className="border-2 border-gray-100 hover:border-[#FF7F7F] transition-all hover:shadow-xl h-full">
                <CardContent className="p-8">
                  <div className="flex items-center mb-4">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-16 h-16 rounded-full object-cover mr-4"
                    />
                    <div>
                      <h4 className="font-semibold text-[#4B0082]">{testimonial.name}</h4>
                      <p className="text-sm text-gray-600">{testimonial.role}</p>
                    </div>
                  </div>
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i} className="text-[#FF7F7F] text-xl">★</span>
                    ))}
                  </div>
                  <p className="text-gray-600 italic leading-relaxed">"{testimonial.text}"</p>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Buttons */}
      <button
        onClick={scrollPrev}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-[#4B0082] hover:bg-[#FF7F7F] hover:text-white transition-all z-10"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>
      <button
        onClick={scrollNext}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-[#4B0082] hover:bg-[#FF7F7F] hover:text-white transition-all z-10"
      >
        <ChevronRight className="h-6 w-6" />
      </button>

      {/* Dots Indicator */}
      <div className="flex justify-center gap-2 mt-8">
        {testimonials.map((_, index) => (
          <button
            key={index}
            className={`w-2 h-2 rounded-full transition-all ${
              index === selectedIndex % testimonials.length
                ? 'bg-[#FF7F7F] w-8'
                : 'bg-gray-300'
            }`}
            onClick={() => emblaApi && emblaApi.scrollTo(index)}
          />
        ))}
      </div>
    </div>
  );
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);

  if (!service) {
    return <Navigate to="/" replace />;
  }

  const pricing = pricingPackages[slug] || [];
  const serviceFaqs = faqs[slug] || faqs.general;

  return (
    <>
      <Helmet>
        <title>{service.title} Services - Expert Help | Trademark Frontline</title>
        <meta name="description" content={`Professional ${service.title.toLowerCase()} services. ${service.description.substring(0, 100)}...`} />
      </Helmet>
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#4B0082]/5 to-[#FF7F7F]/10 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block bg-[#FF7F7F]/10 text-[#FF7F7F] px-4 py-2 rounded-full text-sm font-semibold">
                {service.title}
              </div>
              <h1 className="text-5xl font-bold text-[#4B0082] leading-tight">
                Professional {service.title} Services
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                {service.fullDescription}
              </p>
              <Button
                size="lg"
                className="bg-[#FF7F7F] hover:bg-[#FF6B6B] text-white px-8 py-6 text-lg transition-all transform hover:scale-105"
                onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Get Started - From $45
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>

            <div className="relative image-zoom rounded-2xl overflow-hidden shadow-2xl hover-lift">
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#4B0082]/40 to-transparent"></div>
            </div>
          </div>
        </div>
      </section>

      {/* About Service Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FF7F7F] font-semibold text-sm mb-2">ABOUT THIS SERVICE</p>
            <h2 className="text-4xl font-bold text-[#4B0082] mb-4">
              Comprehensive {service.title}
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed">
              {service.fullDescription}
            </p>
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FF7F7F] font-semibold text-sm mb-2">HOW WE WORK</p>
            <h2 className="text-4xl font-bold text-[#4B0082] mb-4">
              Our Simple Three-Step Process
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              {service.howWeWork.description}
            </p>
          </div>

          <div className="relative">
            {/* Connection Lines - Desktop Only */}
            <div className="hidden md:block absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-[#FF7F7F] via-[#FF7F7F] to-[#FF7F7F] mx-[20%]"></div>

            <ResponsiveCarousel
              items={service.howWeWork.steps}
              renderItem={(step, index) => (
                <div className="relative">
                  <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-[#FF7F7F]/20 hover:border-[#FF7F7F] transition-all hover:transform hover:scale-105">
                    <div className="w-16 h-16 bg-[#FF7F7F] text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6 mx-auto relative z-10">
                      {index + 1}
                    </div>
                    <h3 className="text-xl font-bold text-[#4B0082] mb-4 text-center">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 text-center">{step.description}</p>
                  </div>
                </div>
              )}
              gridCols="md:grid-cols-3"
              gap="gap-8"
            />
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      {pricing.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <p className="text-[#FF7F7F] font-semibold text-sm mb-2">PRICING PACKAGES</p>
              <h2 className="text-4xl font-bold text-[#4B0082] mb-4">
                Choose Your Package
              </h2>
              <p className="text-gray-600 text-lg max-w-3xl mx-auto">
                Select the package that best fits your needs. All packages include expert support and guidance.
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-6 max-w-7xl mx-auto">
              {pricing.map((pkg, index) => (
                <Card
                  key={index}
                  className={`relative overflow-hidden transition-all duration-300 flex flex-col w-full md:w-[calc(50%-12px)] lg:w-[calc(25%-18px)] ${
                    pkg.popular
                      ? 'border-4 border-[#FF7F7F] shadow-2xl transform scale-105'
                      : 'border-2 border-gray-200 hover:border-[#FF7F7F] hover:shadow-xl'
                  }`}
                >
                  {pkg.popular && (
                    <div className="absolute top-0 right-0 bg-[#FF7F7F] text-white px-4 py-1 text-sm font-semibold">
                      POPULAR
                    </div>
                  )}
                  <CardContent className="p-6 flex flex-col flex-grow">
                    <h3 className="text-2xl font-bold text-[#4B0082] mb-2">{pkg.name}</h3>
                    <p className="text-sm text-gray-600 mb-4 min-h-[3rem]">{pkg.description}</p>
                    <div className="mb-6">
                      <div className="flex items-baseline">
                        <span className="text-5xl font-bold text-[#4B0082]">${pkg.price}</span>
                        {pkg.originalPrice && (
                          <span className="text-2xl text-gray-400 line-through ml-2">${pkg.originalPrice}</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">+govt fees</p>
                    </div>
                    <div className="max-h-96 overflow-y-auto mb-6 space-y-2 pr-2 flex-grow" style={{ scrollbarWidth: 'thin', scrollbarColor: '#FF7F7F #f1f1f1' }}>
                      {pkg.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-[#FF7F7F] mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-gray-600 leading-relaxed">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <Button
                      className={`w-full mt-auto ${
                        pkg.popular
                          ? 'bg-[#FF7F7F] hover:bg-[#FF6B6B]'
                          : 'bg-[#4B0082] hover:bg-[#3A0062]'
                      } text-white transition-all`}
                      onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                    >
                      Get Started
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FF7F7F] font-semibold text-sm mb-2">WHY CHOOSE US</p>
            <h2 className="text-4xl font-bold text-[#4B0082] mb-4">
              Why Work With Trademark Frontline
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto mb-8">
              Whether you're just starting out or expanding your business, Trademark Frontline is your dedicated partner. We offer clear communication, expert guidance, and a hassle-free experience every step of the way.
            </p>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Our team brings decades of combined experience in trademark law, copyright protection, and business formation. We understand the unique challenges businesses face and provide tailored solutions that work.
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <ResponsiveCarousel
              items={service.whyChooseUs}
              renderItem={(point, index) => (
                <Card className="border-2 border-transparent hover:border-[#FF7F7F] transition-all hover:shadow-xl hover-lift animate-scale-in group" style={{ animationDelay: `${index * 0.15}s` }}>
                  <CardContent className="p-8">
                    <h3 className="text-xl font-bold text-[#4B0082] mb-4 group-hover:text-[#FF7F7F] transition-colors">{point.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{point.description}</p>
                  </CardContent>
                </Card>
              )}
              gridCols="md:grid-cols-2"
              gap="gap-8"
            />
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FF7F7F] font-semibold text-sm mb-2">TESTIMONIALS</p>
            <h2 className="text-4xl font-bold text-[#4B0082] mb-4">What Our Clients Say</h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Hear from satisfied clients who have protected their brands with our services.
            </p>
          </div>

          <TestimonialsCarousel testimonials={testimonials} />
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FF7F7F] font-semibold text-sm mb-2">FAQs</p>
            <h2 className="text-4xl font-bold text-[#4B0082] mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-600 text-lg">
              Find answers to common questions about {service.title.toLowerCase()}.
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {serviceFaqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-white border-2 border-gray-100 rounded-lg px-6 hover:border-[#FF7F7F] transition-all"
              >
                <AccordionTrigger className="text-left font-semibold text-[#4B0082] hover:text-[#FF7F7F]">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#FF7F7F] to-[#FF6B6B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h2 className="text-4xl font-bold mb-6">
                Ready to Protect Your Brand?
              </h2>
              <p className="text-xl mb-8 text-white/90 leading-relaxed">
                Don't wait to secure your brand's future. Start your {service.title.toLowerCase()} application today with Trademark Frontline's expert team. Fast, reliable, and affordable protection starts here.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <CheckCircle2 className="h-6 w-6 mr-3" />
                  <span className="text-lg">Special Offer: Starting at $45</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle2 className="h-6 w-6 mr-3" />
                  <span className="text-lg">Expert Guidance Every Step</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle2 className="h-6 w-6 mr-3" />
                  <span className="text-lg">98% Approval Success Rate</span>
                </div>
              </div>
            </div>

            <div>
              <ContactForm
                title={`Start Your ${service.title}`}
                ctaText="Get Started Now"
                showMessage={true}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default ServiceDetail;